import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import { getProfilePictureUrlOptions } from '@api/generated/@tanstack/react-query.gen';

type AvatarSize = 'sm' | 'md' | 'lg';

interface Props {
  profilePictureUrl: string | null;
  displayName: string;
  size?: AvatarSize;
  className?: string;
}

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/**
 * Avatar that resolves a presigned S3 URL when authenticated. Falls back to
 * initials for logged-out viewers (the presigned-URL endpoint requires auth).
 */
export function UserAvatar({
  profilePictureUrl,
  displayName,
  size = 'md',
  className,
}: Props) {
  const { isAuthenticated } = useAuth();
  const enabled = isAuthenticated && !!profilePictureUrl;

  const { data, isLoading, isError } = useQuery({
    ...getProfilePictureUrlOptions({ path: { s3Key: profilePictureUrl ?? '' } }),
    enabled,
  });

  const imageUrl = enabled ? data?.url : undefined;

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {imageUrl && !isError && <AvatarImage src={imageUrl} alt={displayName} />}
      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
        {enabled && isLoading ? '…' : getInitials(displayName)}
      </AvatarFallback>
    </Avatar>
  );
}
